import { TextField } from "@radix-ui/themes";
import { useCallback, useMemo } from "react";
import Select from "@/components/shared/select";
import { EditableTableField, EditableTableRow } from "./EditableTable.model";

const commonFieldProps = {
  size: "2" as const,
  variant: "soft" as const,
  className: "w-full rounded-none bg-transparent",
};

interface EditableTableFieldRenderProps {
  field: EditableTableField;
  row: EditableTableRow;
  updateRow: (id: string, updates: Partial<EditableTableRow>) => void;
  shouldStrikeOut?: boolean;
}

export function EditableTableFieldRender({
  field,
  row,
  updateRow,
  shouldStrikeOut = false,
}: EditableTableFieldRenderProps) {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateRow(row.id, { [field.key]: e.target.value });
    },
    [updateRow, row.id, field.key]
  );

  const handleSelectChange = useCallback(
    (value: string) => {
      updateRow(row.id, { [field.key]: value });
    },
    [updateRow, row.id, field.key]
  );

  const props = useMemo(
    () => ({
      ...commonFieldProps,
      placeholder: field.placeholder || `Enter ${field.label.toLowerCase()}`,
      value: row[field.key] || "",
      onChange: handleChange,
      disabled: !row.enabled,
      id: field.key,
      dataFieldType: field.type,
      className: `${commonFieldProps.className} ${
        shouldStrikeOut ? "line-through text-gray-9" : ""
      }`,
    }),
    [
      field.placeholder,
      field.label,
      field.key,
      field.type,
      row[field.key],
      row.enabled,
      handleChange,
      shouldStrikeOut,
    ]
  );

  switch (field.type) {
    case "number":
      return <TextField.Root {...props} type="number" />;
    case "select":
      return (
        <div className={shouldStrikeOut ? "line-through text-gray-9" : ""}>
          <Select
            value={row[field.key] || ""}
            onChange={handleSelectChange}
            options={field.options || []}
            placeholder={
              field.placeholder || `Select ${field.label.toLowerCase()}`
            }
            classes={{ root: "w-full" }}
            size="medium"
          />
        </div>
      );
    case "multiselect":
      // For now, treat multiselect as text input - can be enhanced later
      return (
        <TextField.Root
          {...props}
          placeholder={
            field.placeholder ||
            `Enter ${field.label.toLowerCase()} (comma-separated)`
          }
        />
      );
    default:
      return <TextField.Root {...props} />;
  }
}
